import React, { useState, useEffect, useCallback } from 'react';
import { familiesApi, configApi, studentsApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Loader2, Search, Trash2, Pencil, X, UsersRound, Percent, ShieldCheck, UserPlus, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const titleize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// Display labels for the (internally-named) husband/wife member keys.
const memberLabel = (k) => (k === 'husband' ? 'Father' : k === 'wife' ? 'Mother' : titleize(k));

const emptyMember = () => ({ name: '', phone: '', email: '', occupation: '', employer: '', nationality: '' });
const emptyForm = (branch = '') => ({
  family_name: '',
  branch,
  status: 'active',
  husband: emptyMember(),
  wife: emptyMember(),
  address: '',
  city: '',
  country: '',
  notes: '',
  guardian: 'husband',
  guardian_name: '',
  guardian_phone: '',
  guardian_email: '',
  auto_discounts: [],
});

const FamiliesPage = () => {
  const { isAdmin, role, userBranches } = useAuth();
  // accountant_assistant has add-only access: it can register families but not
  // edit/delete or manage a family's students (backend keeps those admin/manager).
  const canManageFamilies = isAdmin || role === 'manager';
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [discountTypes, setDiscountTypes] = useState([]);

  const [filters, setFilters] = useState({ search: '', branch: 'all', guardian: 'all', discount_type: 'all', status: 'all' });

  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Load config (branches + discount types)
  useEffect(() => {
    (async () => {
      try {
        const cfg = await configApi.get();
        setBranches(isAdmin ? (cfg.data.branches || []) : (userBranches || []));
      } catch { /* noop */ }
      try {
        const dt = await api.get('/config/discount-types');
        setDiscountTypes(dt.data.discount_types || []);
      } catch { /* noop */ }
    })();
  }, [isAdmin, userBranches]);

  const fetchFamilies = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.branch !== 'all') params.branch = filters.branch;
      if (filters.guardian !== 'all') params.guardian = filters.guardian;
      if (filters.discount_type !== 'all') params.discount_type = filters.discount_type;
      if (filters.status !== 'all') params.status = filters.status;
      const res = await familiesApi.getAll(params);
      setFamilies(res.data || []);
    } catch (err) {
      toast.error('Failed to load families');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchFamilies, filters.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchFamilies, filters.search]);

  const clearFilters = () => setFilters({ search: '', branch: 'all', guardian: 'all', discount_type: 'all', status: 'all' });
  const hasFilters = filters.search || filters.branch !== 'all' || filters.guardian !== 'all' || filters.discount_type !== 'all' || filters.status !== 'all';

  // ---- form helpers ----
  const setMember = (who, field, value) => setForm((f) => ({ ...f, [who]: { ...f[who], [field]: value } }));
  const addDiscountRow = () => setForm((f) => ({ ...f, auto_discounts: [...f.auto_discounts, { discount_type: '', percentage: '' }] }));
  const updateDiscountRow = (i, field, value) => setForm((f) => {
    const next = [...f.auto_discounts];
    next[i] = { ...next[i], [field]: value };
    return { ...f, auto_discounts: next };
  });
  const removeDiscountRow = (i) => setForm((f) => ({ ...f, auto_discounts: f.auto_discounts.filter((_, idx) => idx !== i) }));

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm(isAdmin ? '' : (userBranches?.[0] || '')));
    setShowDialog(true);
  };
  const openEdit = (fam) => {
    setEditingId(fam.id);
    setForm({
      ...emptyForm(),
      ...fam,
      husband: { ...emptyMember(), ...(fam.husband || {}) },
      wife: { ...emptyMember(), ...(fam.wife || {}) },
      auto_discounts: (fam.auto_discounts || []).map((d) => ({ discount_type: d.discount_type, percentage: d.percentage })),
    });
    setShowDialog(true);
  };

  const saveFamily = async () => {
    if (!form.family_name.trim()) { toast.error('Family name is required'); return; }
    // validate discount rows
    for (const d of form.auto_discounts) {
      if (!d.discount_type) { toast.error('Pick a discount type for each auto-discount row'); return; }
    }
    const payload = {
      ...form,
      auto_discounts: form.auto_discounts
        .filter((d) => d.discount_type)
        .map((d) => ({ discount_type: d.discount_type, percentage: parseFloat(d.percentage) || 0 })),
    };
    setSaving(true);
    try {
      if (editingId) {
        await familiesApi.update(editingId, payload);
        toast.success('Family updated');
      } else {
        const res = await familiesApi.create(payload);
        toast.success(`Family registered (${res.data.family_code})`);
      }
      setShowDialog(false);
      fetchFamilies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save family');
    } finally {
      setSaving(false);
    }
  };

  const deleteFamily = async (fam) => {
    if (!window.confirm(`Delete family "${fam.family_name}" (${fam.family_code})? This cannot be undone.`)) return;
    try {
      await familiesApi.delete(fam.id);
      toast.success('Family deleted');
      fetchFamilies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete family');
    }
  };

  // ---- manage students dialog ----
  const [studentsDialog, setStudentsDialog] = useState(false);
  const [studentsFamily, setStudentsFamily] = useState(null);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [linkingId, setLinkingId] = useState(null);

  const loadLinked = useCallback(async (familyId) => {
    setLinkedLoading(true);
    try {
      const r = await familiesApi.getStudents(familyId);
      setLinkedStudents(r.data || []);
    } catch { setLinkedStudents([]); } finally { setLinkedLoading(false); }
  }, []);

  const openStudents = (fam) => {
    setStudentsFamily(fam);
    setStudentSearch('');
    setStudentResults([]);
    setStudentsDialog(true);
    loadLinked(fam.id);
  };

  // search candidate students to link
  useEffect(() => {
    if (!studentsDialog || studentSearch.trim().length < 2) { setStudentResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await studentsApi.getAll({ search: studentSearch, limit: 10 });
        setStudentResults(r.data?.students || []);
      } catch { setStudentResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [studentSearch, studentsDialog]);

  const linkStudent = async (student) => {
    setLinkingId(student.id);
    try {
      const r = await familiesApi.linkStudents(studentsFamily.id, [student.id]);
      toast.success(r.data.message);
      setStudentSearch(''); setStudentResults([]);
      await loadLinked(studentsFamily.id);
      fetchFamilies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link student');
    } finally { setLinkingId(null); }
  };

  const unlinkStudent = async (st) => {
    if (!window.confirm(`Unlink ${st.name} from ${studentsFamily.family_name}? This removes the family's auto-discounts from the student.`)) return;
    try {
      const r = await familiesApi.unlinkStudent(studentsFamily.id, st.id);
      toast.success(r.data.message);
      await loadLinked(studentsFamily.id);
      fetchFamilies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink student');
    }
  };

  const linkedIds = new Set(linkedStudents.map((s) => s.id));
  const withDiscounts = families.filter((f) => (f.auto_discounts || []).length > 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1D] flex items-center gap-2">
            <UsersRound className="w-6 h-6 text-[#3E3F96]" /> Family Registration
          </h1>
          <p className="text-sm text-[#5C635F] mt-1">Register a family before adding students — parents, guardian and auto-applied discounts.</p>
        </div>
        <Button onClick={openAdd} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-family-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Family
        </Button>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-[#D8D9E8]"><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-[#3E3F96]/10 rounded-lg"><UsersRound className="w-5 h-5 text-[#3E3F96]" /></div>
          <div><p className="text-xs text-[#5C635F]">Total Families</p><p className="text-xl font-bold text-[#1C1F1D]">{families.length}</p></div>
        </CardContent></Card>
        <Card className="border-[#D8D9E8]"><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg"><ShieldCheck className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-xs text-[#5C635F]">Active</p><p className="text-xl font-bold text-[#1C1F1D]">{families.filter((f) => (f.status || 'active') === 'active').length}</p></div>
        </CardContent></Card>
        <Card className="border-[#D8D9E8]"><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg"><Percent className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-xs text-[#5C635F]">With Auto-Discounts</p><p className="text-xl font-bold text-[#1C1F1D]">{withDiscounts}</p></div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-[#5C635F]">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#9CA3AF]" />
                <Input className="pl-8" placeholder="Name, code, parent or phone..."
                  value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="family-search" />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs text-[#5C635F]">Branch</Label>
              <Select value={filters.branch} onValueChange={(v) => setFilters({ ...filters, branch: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs text-[#5C635F]">Guardian</Label>
              <Select value={filters.guardian} onValueChange={(v) => setFilters({ ...filters, guardian: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="husband">Father</SelectItem>
                  <SelectItem value="wife">Mother</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Label className="text-xs text-[#5C635F]">Auto-Discount</Label>
              <Select value={filters.discount_type} onValueChange={(v) => setFilters({ ...filters, discount_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {discountTypes.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label className="text-xs text-[#5C635F]">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="h-10"><X className="w-4 h-4 mr-1" /> Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F5FA]">
                <TableHead className="text-xs uppercase font-semibold">Code</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Family</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Father</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Mother</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Guardian</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Auto-Discounts</TableHead>
                <TableHead className="text-xs uppercase font-semibold">Students</TableHead>
                <TableHead className="text-xs uppercase font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#3E3F96]" /></TableCell></TableRow>
              ) : families.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-[#5C635F]">No families found. Click "Add Family" to register one.</TableCell></TableRow>
              ) : families.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-medium text-[#3E3F96]">{f.family_code}</TableCell>
                  <TableCell className="font-medium">{f.family_name}{(f.status || 'active') !== 'active' && <Badge variant="outline" className="ml-2 text-[10px]">Inactive</Badge>}</TableCell>
                  <TableCell>{f.branch || '-'}</TableCell>
                  <TableCell className="text-sm">{f.husband?.name || '-'}<div className="text-xs text-[#5C635F]">{f.husband?.phone}</div></TableCell>
                  <TableCell className="text-sm">{f.wife?.name || '-'}<div className="text-xs text-[#5C635F]">{f.wife?.phone}</div></TableCell>
                  <TableCell className="text-sm">{f.guardian_display_name || '-'}<div className="text-xs text-[#5C635F]">{memberLabel(f.guardian_role)}</div></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {(f.auto_discounts || []).length === 0 ? <span className="text-xs text-[#9CA3AF]">None</span> :
                        f.auto_discounts.map((d, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] border-[#3E3F96]/40 text-[#3E3F96]">{titleize(d.discount_type)} {d.percentage ? `${d.percentage}%` : ''}</Badge>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {canManageFamilies ? (
                      <Button variant="ghost" size="sm" onClick={() => openStudents(f)} className="gap-1 text-[#3E3F96]" title="Manage students">
                        <GraduationCap className="w-4 h-4" /> {f.student_count || 0}
                      </Button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-sm text-[#5C635F]"><GraduationCap className="w-4 h-4" /> {f.student_count || 0}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManageFamilies && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(f)} title="Edit"><Pencil className="w-4 h-4 text-[#3E3F96]" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteFamily(f)} title="Delete"><Trash2 className="w-4 h-4 text-[#C85A40]" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Family' : 'Register New Family'}</DialogTitle></DialogHeader>

          <div className="space-y-5">
            {/* Family basics */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-[#3E3F96]">Family Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label>Family Name *</Label>
                  <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} placeholder="e.g. Mukasa Family" />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Select value={form.branch || 'none'} onValueChange={(v) => setForm({ ...form, branch: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Father & Mother (stored internally as husband/wife) */}
            {['husband', 'wife'].map((who) => (
              <section key={who} className="space-y-3">
                <h3 className="text-sm font-semibold text-[#3E3F96]">{memberLabel(who)} Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label>Name</Label><Input value={form[who].name} onChange={(e) => setMember(who, 'name', e.target.value)} /></div>
                  <div><Label>Phone</Label><Input value={form[who].phone} onChange={(e) => setMember(who, 'phone', e.target.value)} /></div>
                  <div><Label>Email</Label><Input value={form[who].email} onChange={(e) => setMember(who, 'email', e.target.value)} /></div>
                  <div><Label>Occupation</Label><Input value={form[who].occupation} onChange={(e) => setMember(who, 'occupation', e.target.value)} /></div>
                  <div><Label>Employer</Label><Input value={form[who].employer} onChange={(e) => setMember(who, 'employer', e.target.value)} /></div>
                  <div><Label>Nationality</Label><Input value={form[who].nationality} onChange={(e) => setMember(who, 'nationality', e.target.value)} /></div>
                </div>
              </section>
            ))}

            {/* Address */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-[#3E3F96]">Address & Basic Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
              </div>
            </section>

            {/* Guardian */}
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-[#3E3F96]">Guardian for Students</h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <Label>Guardian is</Label>
                  <Select value={form.guardian} onValueChange={(v) => setForm({ ...form, guardian: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="husband">Father</SelectItem>
                      <SelectItem value="wife">Mother</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.guardian === 'other' ? (
                  <>
                    <div><Label>Guardian Name</Label><Input value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} /></div>
                    <div><Label>Guardian Phone</Label><Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} /></div>
                    <div><Label>Guardian Email</Label><Input value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} /></div>
                  </>
                ) : (
                  <div className="sm:col-span-3 text-sm text-[#5C635F] pb-2">
                    Guardian contact will use the <span className="font-medium">{memberLabel(form.guardian)}</span>'s details:
                    <span className="ml-1 font-medium">{form[form.guardian]?.name || '(name not set)'}</span>
                    {form[form.guardian]?.phone ? ` · ${form[form.guardian].phone}` : ''}
                  </div>
                )}
              </div>
            </section>

            {/* Auto Discounts */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#3E3F96]">Auto-Applied Discounts</h3>
                <Button type="button" variant="outline" size="sm" onClick={addDiscountRow}><Plus className="w-4 h-4 mr-1" /> Add Discount</Button>
              </div>
              <p className="text-xs text-[#5C635F]">Picked from the Discount module. When a student is linked, these are raised as <span className="font-medium">pending discount requests</span> for an admin to approve.</p>
              {form.auto_discounts.length === 0 ? (
                <p className="text-sm text-[#9CA3AF]">No auto-discounts. Click "Add Discount" to add one.</p>
              ) : form.auto_discounts.map((d, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Discount Type</Label>
                    <Select value={d.discount_type || ''} onValueChange={(v) => updateDiscountRow(i, 'discount_type', v)}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {discountTypes.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">Percentage</Label>
                    <Input type="number" min="0" max="100" step="0.01" value={d.percentage}
                      onChange={(e) => updateDiscountRow(i, 'percentage', e.target.value)} placeholder="%" />
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeDiscountRow(i)} className="mb-0.5"><Trash2 className="w-4 h-4 text-[#C85A40]" /></Button>
                </div>
              ))}
            </section>

            {/* Notes */}
            <section className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional family notes..." />
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveFamily} disabled={saving} className="bg-[#3E3F96] hover:bg-[#2D2E7A]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? 'Save Changes' : 'Register Family'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Students Dialog */}
      <Dialog open={studentsDialog} onOpenChange={setStudentsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Students — {studentsFamily?.family_name} ({studentsFamily?.family_code})</DialogTitle>
          </DialogHeader>

          {/* Auto-discount reminder */}
          <div className="text-xs text-[#5C635F] -mt-1">
            {(studentsFamily?.auto_discounts || []).length > 0
              ? <>Linking a student requests these for approval: {studentsFamily.auto_discounts.map((d) => `${titleize(d.discount_type)} ${d.percentage ? d.percentage + '%' : ''}`).join(', ')}</>
              : 'This family has no auto-discounts; linking just records the family.'}
          </div>

          {/* Add existing student */}
          <div className="space-y-2">
            <Label>Add an existing student</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#9CA3AF]" />
              <Input className="pl-8" placeholder="Search student by name or ID (min 2 chars)..."
                value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} />
            </div>
            {studentResults.length > 0 && (
              <div className="border border-[#D8D9E8] rounded-md divide-y max-h-52 overflow-y-auto">
                {studentResults.map((st) => {
                  const already = linkedIds.has(st.id);
                  return (
                    <div key={st.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <span className="font-medium">{st.first_name} {st.last_name}</span>
                        <span className="text-xs text-[#5C635F] ml-2">{st.student_id} · {st.branch} · {st.current_class}</span>
                        {st.family_code && st.family_code !== studentsFamily?.family_code && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-amber-600 border-amber-300">in {st.family_code}</Badge>
                        )}
                      </div>
                      {already ? (
                        <span className="text-xs text-emerald-600">Linked</span>
                      ) : (
                        <Button size="sm" variant="outline" disabled={linkingId === st.id} onClick={() => linkStudent(st)}>
                          {linkingId === st.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Link</>}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Linked students */}
          <div className="space-y-2">
            <Label>Linked students ({linkedStudents.length})</Label>
            {linkedLoading ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#3E3F96]" /></div>
            ) : linkedStudents.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No students linked yet.</p>
            ) : (
              <div className="border border-[#D8D9E8] rounded-md divide-y">
                {linkedStudents.map((st) => (
                  <div key={st.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium">{st.name}</span>
                      <span className="text-xs text-[#5C635F] ml-2">{st.student_id} · {st.branch} · {st.current_class}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => unlinkStudent(st)} title="Unlink">
                      <X className="w-4 h-4 text-[#C85A40]" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStudentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamiliesPage;
